// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Wand2,
  Upload,
  FileSpreadsheet,
  X,
  RotateCcw,
  Sparkles,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  createAgentSession,
  uploadAgentFile,
  sendAgentMessage,
  watchAgentSession,
  getAgentSession,
  selectAgentSheet,
} from '../../utils/agentApi';
import ToolCallBlock from './ToolCallBlock';
import FinalMappingCardBS from './FinalMappingCardBS';
import SheetPickerBS from './SheetPickerBS';
import './Agent.css';

const newId = () =>
  `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

// The single instruction we send the agent when a file lands. The
// system prompt does the rest — there's no conversation, just a kick.
const AUTO_INSTRUCTION =
  'A balance sheet has been uploaded. Run the full mapping pipeline end-to-end on it now, mapping every detected period. Do not ask any questions.';

const BalanceSheetAgent = ({ userEmail, initialSessionId, onSessionIdChange, onSessionsRefresh }) => {
  const [sessionId, setSessionId] = useState(initialSessionId || null);
  const [files, setFiles] = useState([]);
  const [chatItems, setChatItems] = useState([]);
  // Start in 'loading' when the caller asked us to restore a session — if
  // we left this at 'idle' the EmptyState dropzone would briefly render
  // during the first commit (one frame), which reads as the agent
  // "restarting" rather than resuming a past run.
  const [phase, setPhase] = useState(initialSessionId ? 'loading' : 'idle'); // idle | reading | uploading | sheet_selection | running | done | error | loading
  const [error, setError] = useState(null);
  // When phase === 'sheet_selection', this holds the just-uploaded
  // workbook's tab list so the picker can render. Cleared on reset.
  const [pendingSheets, setPendingSheets] = useState(null);
  // Filename of the file being ingested, captured synchronously on drop so
  // the 'reading' loading view can name the file before the upload+parse
  // round-trip resolves. Cleared on reset.
  const [loadingFile, setLoadingFile] = useState(null);
  const abortRef = useRef(null);
  // Track which sessionId we've already restored so a re-render with the
  // same prop doesn't refetch and clobber in-progress runs.
  const restoredSessionRef = useRef(null);
  // Last event id received from the SSE stream. Used as the reattach
  // offset if the watch reconnects (refresh, tab close + reopen). The
  // server stamps every event with its Redis Stream id; we just store
  // the most recent one. '0' means "from the beginning of the stream."
  const lastEventIdRef = useRef('0');

  // Latest sidebar-refresh callback, read from inside the long-lived SSE
  // onEvent closure without re-creating watchSession. Fired once per run
  // (guarded by runActivityNotifiedRef) the moment the first event arrives
  // so the new session appears in the sidebar — tagged "running" — without
  // the user having to navigate away first.
  const onSessionsRefreshRef = useRef(onSessionsRefresh);
  useEffect(() => {
    onSessionsRefreshRef.current = onSessionsRefresh;
  }, [onSessionsRefresh]);
  const runActivityNotifiedRef = useRef(false);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    setSessionId(null);
    setFiles([]);
    setChatItems([]);
    setPhase('idle');
    setError(null);
    setPendingSheets(null);
    setLoadingFile(null);
    restoredSessionRef.current = null;
    onSessionIdChange?.(null);
  }, [onSessionIdChange]);

  // Attach to a session's event stream. Used by both runAgent (after
  // kicking off a fresh run) and the mount-with-initialSessionId effect
  // (reattach to a session that's already running on the server). The
  // `fromEventId` is the offset to start at — '0' for a fresh run, the
  // session's `_last_event_id` for a reattach.
  const watchSession = useCallback(async (sid, fromEventId, signal) => {
    lastEventIdRef.current = fromEventId || '0';
    runActivityNotifiedRef.current = false;
    await watchAgentSession(
      sid,
      lastEventIdRef.current,
      (event, eventId) => {
        if (eventId) lastEventIdRef.current = eventId;
        // First event of the run = the agent is actively working and the
        // session row now exists in Postgres (upload wrote it). Nudge the
        // sidebar to refetch so this session shows up tagged "running"
        // without the user navigating away. Fires once per attach; App's
        // 5s status poll takes over from there to flip running → completed.
        if (!runActivityNotifiedRef.current) {
          runActivityNotifiedRef.current = true;
          onSessionsRefreshRef.current?.();
        }
        handleAgentEvent(event, setChatItems);
      },
      signal,
    );
  }, []);

  // Swap the latest mapping_result's mapping payload in-place. Called by
  // FinalMappingCard after a successful PATCH so the recomputed
  // reconciliation, period_breakdowns, and edited categories propagate
  // through to the downstream Excel export (which reads the same prop).
  const handleMappingUpdated = useCallback((updatedMapping) => {
    setChatItems((prev) => {
      // Find the latest mapping_result and replace its mapping. Walking
      // bottom-up handles continuation turns where multiple
      // mapping_result items may be present.
      for (let i = prev.length - 1; i >= 0; i--) {
        if (prev[i]?.type === 'mapping_result') {
          const next = prev.slice();
          next[i] = { ...prev[i], mapping: updatedMapping };
          return next;
        }
      }
      return prev;
    });
  }, []);

  const runAgent = useCallback(async (sid) => {
    setPhase('running');
    const ac = new AbortController();
    abortRef.current = ac;
    try {
      // Fire-and-forget: the POST returns once the background task has
      // been spawned. The agent run survives client disconnect.
      const sendResult = await sendAgentMessage(sid, AUTO_INSTRUCTION, 'bs');
      // 409 means another tab already started this session. Both tabs
      // converge on the same event stream, replaying from '0' so this
      // tab sees the full chat history too.
      await watchSession(sid, '0', ac.signal);
      setPhase('done');
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message || String(err));
        setPhase('error');
      } else {
        setPhase('done');
      }
    } finally {
      abortRef.current = null;
      setChatItems((prev) =>
        prev.map((it) =>
          it.type === 'assistant' && it.isStreaming
            ? { ...it, isStreaming: false }
            : it.type === 'tool_group' && it.isRunning
              ? { ...it, isRunning: false }
              : it,
        ),
      );
    }
  }, [watchSession]);

  const startWithSheet = useCallback(
    async (sid, fileId, sheetName) => {
      await selectAgentSheet(sid, fileId, sheetName);
      // Reflect the chosen sheet on the file chip — backend persisted it
      // to the file_ref, but our local copy is stale.
      setFiles((prev) =>
        prev.map((f) =>
          f.file_id === fileId ? { ...f, sheet_name: sheetName } : f,
        ),
      );
      setPendingSheets(null);
      await runAgent(sid);
    },
    [runAgent],
  );

  const onDrop = useCallback(
    async (accepted) => {
      if (!accepted.length) return;
      setError(null);
      // Name the file immediately — we hold the File object here, so the
      // 'reading' loading view never renders anonymously while the session
      // is created and the workbook is parsed for its tabs.
      setLoadingFile(accepted[0]?.name || null);
      setPhase('reading');
      try {
        const { session_id } = await createAgentSession();
        setSessionId(session_id);
        onSessionIdChange?.(session_id);
        restoredSessionRef.current = session_id;
        const file = accepted[0]; // Single file — agent is one-file-at-a-time.
        const uploaded = await uploadAgentFile(session_id, file, 'bs');
        setFiles([uploaded]);

        const sheets = uploaded.sheets || [];
        if (sheets.length <= 1) {
          // Single-sheet (or degenerate empty) workbook: skip the picker,
          // auto-confirm so the user never sees a one-option choice.
          await startWithSheet(session_id, uploaded.file_id, sheets[0]);
        } else {
          setPendingSheets(sheets);
          setPhase('sheet_selection');
        }
      } catch (err) {
        setError(err.message || String(err));
        setPhase('error');
      }
    },
    [startWithSheet, onSessionIdChange],
  );

  const onSheetConfirm = useCallback(
    async (sheetName) => {
      const sid = sessionId;
      const fileId = files[0]?.file_id;
      if (!sid || !fileId) return;
      setError(null);
      setPhase('uploading'); // brief transitional state while we POST
      try {
        await startWithSheet(sid, fileId, sheetName);
      } catch (err) {
        setError(err.message || String(err));
        setPhase('error');
      }
    },
    [sessionId, files, startWithSheet],
  );

  // Restore a session when the user clicks one in the sidebar — or
  // when a refresh brings them back to ``#/agent/session/<id>``.
  //
  // The server returns the persisted ``view_state`` (a snapshot of the
  // chat_items array the reducer produced live) plus the session's
  // current ``status``. If the run is still ``'active'`` we additionally
  // attach to the SSE event stream from the session's
  // ``_last_event_id``, so the user sees tool calls and text continuing
  // to stream in real time — exactly as if the tab had never been
  // closed. Completed/failed sessions just render the snapshot.
  useEffect(() => {
    if (!initialSessionId) {
      // Parent cleared the session id — this happens when the user
      // clicks the "Agent" sidebar button while a past session is
      // loaded. Wipe the timeline back to the empty upload state so the
      // dropzone renders. Skip if we never restored anything.
      if (restoredSessionRef.current) {
        reset();
      }
      return;
    }
    if (restoredSessionRef.current === initialSessionId) return;
    // NOTE: do NOT set restoredSessionRef here. Under React.StrictMode
    // the effect mounts → cleans up → re-mounts synchronously; if we
    // claim the ref before the fetch resolves, the cleanup aborts the
    // in-flight fetch and the second mount early-returns without ever
    // restoring state. The user sees `phase: 'loading'` forever (this
    // exact symptom: GET /agent/sessions/<id> hits the network twice,
    // both 200s, but React state never updates). The ref is claimed
    // below, after a successful response — onDrop still sets it
    // synchronously for the live-session path, which is what the guard
    // above protects.

    // If there was an in-flight run for a different session, cancel it.
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    setError(null);
    setPhase('loading');
    setChatItems([]);
    setFiles([]);

    (async () => {
      try {
        // The server persists `view_state` in lock-step with the live
        // SSE reducer, so the resumed timeline is one SELECT away — no
        // client-side reconstruction, no second request for the mapping.
        const session = await getAgentSession(initialSessionId);
        if (ac.signal.aborted) return;
        restoredSessionRef.current = initialSessionId;
        setSessionId(session.session_id);
        setFiles(session.file_refs || []);
        // Snapshot's chat_items normally already carry the
        // mapping_result for completed runs. But the runtime's terminal
        // emission can fail (assemble_mapping crash, partial commit,
        // pre-fix sessions), leaving the card invisible on reload. The
        // backend now derives final_mapping from the persisted message
        // log as a fallback; if chat_items lacks a mapping_result but
        // the server provided one, splice in a synthetic entry so the
        // final card always renders for terminated sessions.
        const items = (session.view_state?.chat_items || []).slice();
        const hasMapping = items.some((it) => it?.type === 'mapping_result');
        if (!hasMapping && session.final_mapping) {
          items.push({
            id: newId(),
            type: 'mapping_result',
            mapping: session.final_mapping,
          });
        }
        setChatItems(items);

        if (session.status === 'active') {
          // Reattach to the live stream. The `_last_event_id` stamp on
          // view_state tells us where to resume so we never replay
          // events the snapshot already accounts for.
          setPhase('running');
          const reattachFrom =
            session.view_state?._last_event_id || session.last_event_id || '0';
          try {
            await watchSession(initialSessionId, reattachFrom, ac.signal);
            if (!ac.signal.aborted) setPhase('done');
          } catch (err) {
            if (ac.signal.aborted || err.name === 'AbortError') return;
            setError(err.message || String(err));
            setPhase('error');
          } finally {
            // Close any streaming flags the reducer may have left open.
            setChatItems((prev) =>
              prev.map((it) =>
                it.type === 'assistant' && it.isStreaming
                  ? { ...it, isStreaming: false }
                  : it.type === 'tool_group' && it.isRunning
                    ? { ...it, isRunning: false }
                    : it,
              ),
            );
          }
        } else {
          setPhase('done');
        }
      } catch (err) {
        if (ac.signal.aborted) return;
        setError(err?.message || String(err));
        setPhase('error');
      }
    })();

    return () => {
      ac.abort();
    };
    // We deliberately depend ONLY on initialSessionId. `reset` and
    // `watchSession` are stable in intent (their bodies don't change),
    // but their identity rotates whenever the parent re-renders — and
    // the parent re-renders every 5s due to the sidebar polling. Putting
    // them in the deps causes the effect to re-fire on every poll tick,
    // which aborts the in-flight getAgentSession/watchAgentSession
    // fetches and re-starts them, producing the "perpetual loading"
    // behavior the user reported. We pin to initialSessionId only; the
    // closures over `reset` and `watchSession` are still correct because
    // we capture them at effect creation and they don't mutate state we
    // care about.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSessionId]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx',
      ],
      'application/vnd.ms-excel.sheet.macroEnabled.12': ['.xlsm'],
      'application/vnd.ms-excel': ['.xls'],
    },
    multiple: false,
    disabled:
      phase === 'reading' ||
      phase === 'uploading' ||
      phase === 'running' ||
      phase === 'loading' ||
      phase === 'sheet_selection',
  });

  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    [],
  );

  const showEmptyState = phase === 'idle';
  const showWorkbookLoading = phase === 'reading';
  const showSheetPicker = phase === 'sheet_selection';

  return (
    <div className="agent-root">
      <div className="agent-header">
        <div className="agent-header-inner">
          <div className="agent-header-title">
            <Wand2 size={18} />
            <span>Balance Sheet Agent</span>
          </div>
          <div className="agent-header-actions">
            {files.length > 0 && (
              <div className="agent-file-chips">
                {files.map((f) => (
                  <span
                    key={f.file_id}
                    className="agent-file-chip"
                    title={
                      f.sheet_name
                        ? `${f.filename} · ${f.sheet_name}`
                        : f.filename
                    }
                  >
                    <FileSpreadsheet size={12} />
                    <span className="agent-file-chip-name">
                      {f.filename}
                      {f.sheet_name ? (
                        <>
                          <span className="agent-file-chip-divider"> · </span>
                          {f.sheet_name}
                        </>
                      ) : null}
                    </span>
                  </span>
                ))}
              </div>
            )}
            {!showEmptyState && (
              <button
                className="agent-newchat-btn"
                onClick={reset}
                disabled={
                  phase === 'running' ||
                  phase === 'uploading' ||
                  phase === 'reading'
                }
                title="Map another file"
              >
                <RotateCcw size={12} style={{ marginRight: 4 }} />
                Map another
              </button>
            )}
          </div>
        </div>
      </div>

      {showEmptyState ? (
        <EmptyState
          getRootProps={getRootProps}
          getInputProps={getInputProps}
          isDragActive={isDragActive}
          phase={phase}
        />
      ) : showWorkbookLoading ? (
        <WorkbookLoading filename={loadingFile} />
      ) : showSheetPicker ? (
        <SheetPickerBS
          file={files[0]}
          sheets={pendingSheets || []}
          onConfirm={onSheetConfirm}
          onCancel={reset}
          submitting={false}
        />
      ) : (
        <RunView
          phase={phase}
          chatItems={chatItems}
          sessionId={sessionId}
          onMappingUpdated={handleMappingUpdated}
        />
      )}

      {error && (
        <div className="agent-error-banner">
          <X size={14} />
          {error}
          <button onClick={() => setError(null)} className="agent-error-dismiss">
            dismiss
          </button>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Empty state — pure drop target
// ---------------------------------------------------------------------------

const EmptyState = ({ getRootProps, getInputProps, isDragActive, phase }) => (
  <div className="agent-empty">
    <div
      {...getRootProps()}
      className={`agent-empty-dropzone ${isDragActive ? 'drag-active' : ''}`}
    >
      <input {...getInputProps()} />
      <div className="agent-empty-icon">
        <Upload size={28} />
      </div>
      <h2 className="agent-empty-title">Drop a balance sheet to map it</h2>
      <p className="agent-empty-sub">
        Drag an Excel file here or click to browse. The agent runs end-to-end
        automatically: it finds Total Assets, Liabilities, and Equity, classifies
        every account across all detected periods, and checks that the sheet balances.
      </p>
      {phase === 'uploading' && (
        <p className="agent-empty-sub agent-empty-status">Uploading…</p>
      )}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Workbook-loading interstitial — calm centered pulse + staged caption.
//
// Fills the gap between the file drop and the sheet picker (or, for
// single-sheet books, the run) while the backend creates the session and
// parses the workbook for its tabs. Owns a tiny caption stepper so the
// status reads as forward motion ("Uploading…" → "Reading workbook…")
// even though the client can't observe the exact parse boundary — both
// captions describe work genuinely in flight during this window. Renders
// as a full-area takeover (sibling to EmptyState), not a stream item.
// ---------------------------------------------------------------------------

const LOADING_STAGES = ['Uploading…', 'Reading workbook…'];

const WorkbookLoading = ({ filename }) => {
  const [stage, setStage] = useState(0);

  // Advance once to the "reading" caption, then park — a slow backend
  // should rest on the final stage, not cycle through captions.
  useEffect(() => {
    const t = setTimeout(
      () => setStage((s) => Math.min(s + 1, LOADING_STAGES.length - 1)),
      950,
    );
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="agent-loading">
      <div className="agent-loading-inner">
        <div className="agent-loading-orb" aria-hidden="true">
          <span className="agent-loading-halo" />
          <span className="agent-loading-halo agent-loading-halo-2" />
          <span className="agent-loading-mark">
            <FileSpreadsheet size={26} />
          </span>
        </div>

        <div className="agent-loading-status" role="status" aria-live="polite">
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={stage}
              className="agent-loading-caption"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            >
              {LOADING_STAGES[stage]}
            </motion.span>
          </AnimatePresence>
        </div>

        {filename && (
          <div className="agent-loading-file" title={filename}>
            <FileSpreadsheet size={12} />
            <span className="agent-loading-file-name">{filename}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Run view — live tool-call progress feed
// ---------------------------------------------------------------------------

// The run alternates tool execution with "thinking" — the gaps where the
// agent reasons or composes its next call. Those gaps render as a single
// breathing line (coral orb + a generic phrase) so the surface never goes
// dead. Two flavors share the SAME look:
//   • ThinkingIndicator — a bodyless breathing line for when the agent is
//     between tools with no reasoning text yet (run start, or right after
//     a tool finishes). Carries the per-gap elapsed counter.
//   • ThoughtTab — the same breathing line, but it owns reasoning text and
//     is expandable. While it's the live tail it breathes identically;
//     once a tool follows, it rests (static coral) into the transcript.
// The phrase reflects the step that JUST finished — never a guess about
// the next tool, which we only learn when tool_start arrives.

const THINKING_PHRASES = {
  get_sheet_structure: 'Studying the statement…',
  find_balance_anchors: 'Locating Assets, Liabilities, and Equity…',
  classify_bs: 'Sorting the next section…',
  read_rows: 'Reading the detail…',
  detect_totals: 'Checking the subtotals…',
  check_balance: 'Checking the balance…',
  run_python: 'Crunching the numbers…',
};

// Generic phrase for the current thinking gap, from the most recent tool
// (scanning back past the active thought, if any). Shared by the bodyless
// indicator and the live ThoughtTab so the line reads the same whether or
// not the agent has started narrating.
const thinkingPhrase = (chatItems) => {
  for (let i = chatItems.length - 1; i >= 0; i--) {
    const it = chatItems[i];
    if (it?.type === 'tool_group' && it.calls?.length) {
      const lastCall = it.calls[it.calls.length - 1];
      return (
        THINKING_PHRASES[lastCall?.toolName] || 'Working through the numbers…'
      );
    }
  }
  return 'Getting oriented…';
};

const ThinkingIndicator = ({ label }) => {
  // Per-gap elapsed seconds. The component mounts fresh for each gap (it's
  // conditionally rendered), so the count restarts each time — exactly the
  // signal we want: "how long is THIS gap taking."
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const start = Date.now();
    const id = setInterval(
      () => setElapsed(Math.floor((Date.now() - start) / 1000)),
      1000,
    );
    return () => clearInterval(id);
  }, []);

  return (
    <motion.div
      className="agent-thinking is-breathing"
      role="status"
      aria-live="polite"
      initial={{ opacity: 0, x: -4 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -4 }}
      transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
    >
      <span className="agent-thinking-orb" aria-hidden="true">
        <span className="agent-thinking-orb-ripple" />
        <span className="agent-thinking-orb-core" />
      </span>
      <span className="agent-thinking-name">Thinking</span>
      <span className="agent-thinking-text">· {label}</span>
      {elapsed >= 1 && (
        <span className="agent-thinking-elapsed">{elapsed}s</span>
      )}
    </motion.div>
  );
};

const RunView = ({ phase, chatItems, sessionId, onMappingUpdated }) => {
  const scrollRef = useRef(null);

  // The breathing "thinking" line bridges every gap between visible
  // actions, and exactly one element carries it at a time:
  //   • when the agent has emitted reasoning text, the tail ThoughtTab
  //     breathes itself (see `active` in the map below), so we DON'T also
  //     show the bodyless indicator — they'd double up.
  //   • when there's no reasoning yet (run start, or right after a tool
  //     finishes), the bodyless ThinkingIndicator fills the gap.
  // Hidden while a tool is actually running and once phase leaves
  // 'running'. `phrase` is shared by both so the line reads identically
  // whether or not the agent has started narrating.
  const lastItem = chatItems[chatItems.length - 1];
  const lastIndex = chatItems.length - 1;
  const phrase = thinkingPhrase(chatItems);
  const showStandalone =
    phase === 'running' &&
    (chatItems.length === 0 ||
      (lastItem?.type === 'tool_group' && !lastItem.isRunning));

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [chatItems, showStandalone]);

  return (
    <div className="agent-run">
      <div className="agent-run-stream" ref={scrollRef}>
        {phase === 'loading' && (
          <div className="agent-run-pending">
            <Sparkles size={14} className="spin-slow" />
            <span>Loading session…</span>
          </div>
        )}

        {chatItems.map((item, idx) => {
          if (item.type === 'tool_group') {
            return (
              <ToolCallBlock
                key={item.id}
                calls={item.calls}
                isRunning={item.isRunning}
              />
            );
          }
          if (item.type === 'mapping_result') {
            return (
              <FinalMappingCardBS
                key={item.id}
                mapping={item.mapping}
                sessionId={sessionId}
                onMappingUpdated={onMappingUpdated}
              />
            );
          }
          if (item.type === 'assistant') {
            const text = (item.text || '').trim();
            if (!text) return null;
            // Breathes only while it's the live tail; once a tool follows
            // it, it rests static into the transcript.
            const active = phase === 'running' && idx === lastIndex;
            return (
              <ThoughtTab
                key={item.id}
                text={text}
                active={active}
                phrase={phrase}
              />
            );
          }
          return null;
        })}

        <AnimatePresence>
          {showStandalone && (
            <ThinkingIndicator key="thinking-indicator" label={phrase} />
          )}
        </AnimatePresence>

        {phase === 'done' && (
          <motion.div
            className="agent-done-pill"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
          >
            <Sparkles size={12} />
            Mapping complete
          </motion.div>
        )}
      </div>
    </div>
  );
};

// Word-by-word fade-in for streaming thought text. Splits the running
// text into word/whitespace segments keyed by index so React preserves
// already-mounted segments (no re-animation) while newly-arriving ones
// run a CSS fade. Disabled when not actively streaming — restored
// thoughts and post-stream renders should appear instantly.
const FadingProse = ({ text, animating }) => {
  const segments = useMemo(
    () => (animating ? text.split(/(\s+)/) : null),
    [text, animating],
  );
  if (!animating) return text;
  return segments.map((seg, i) => (
    <span key={i} className="thought-seg">{seg}</span>
  ));
};

// Distill a long thought into a single-line header summary. Prefer the
// first sentence; otherwise truncate on a word boundary at ~90 chars.
const summarizeThought = (text, max = 90) => {
  const cleaned = text.replace(/\s+/g, ' ').trim();
  if (!cleaned) return '';
  const m = cleaned.match(/^[^.!?]+[.!?](?=\s|$)/);
  const first = m ? m[0] : cleaned;
  if (first.length <= max) {
    return first + (cleaned.length > first.length ? '…' : '');
  }
  const cut = first.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  return (lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut) + '…';
};

// Collapsible "thinking" line for the agent's reasoning. Visually it is
// the SAME breathing line as ThinkingIndicator (coral orb + "Thinking ·
// phrase") — the only additions are a chevron and an expandable body
// holding the prose. Default collapsed: the reasoning is one click away
// but never dominates the stream. Breathes only while it's the live tail
// (`active`); resting thoughts show a static coral mark and their own
// first sentence as the label.
const ThoughtTab = ({ text, active, phrase }) => {
  // Defensive: the system prompt no longer asks the agent for a <mapping>
  // block (the runtime emits a mapping_complete event instead). Strip any
  // stale emission so it can't bleed into the label.
  const cleaned = text.replace(/<mapping>[\s\S]*?<\/mapping>/g, '').trim();
  const [expanded, setExpanded] = useState(false);

  if (!cleaned) return null;
  // While live, show the generic phrase (calm — doesn't jump as text
  // streams in); once settled, show the thought's own first sentence so
  // the transcript stays meaningful.
  const label = active ? phrase : summarizeThought(cleaned);

  return (
    // Plain div — no entry animation. Snapshot-mount paths (reattach
    // mid-run, reopening a completed session) bulk-render many of these at
    // once; a per-item fade can leave them mid-transition. Live items
    // arrive one at a time, so it isn't needed there either.
    <div className="agent-thought">
      <button
        type="button"
        className={`agent-thought-header ${active ? 'is-breathing' : ''}`}
        onClick={() => setExpanded((e) => !e)}
      >
        <span className="agent-thinking-orb" aria-hidden="true">
          <span className="agent-thinking-orb-ripple" />
          <span className="agent-thinking-orb-core" />
        </span>
        <span className="agent-thinking-name">Thinking</span>
        <span className="agent-thinking-text">· {label}</span>
        {expanded ? (
          <ChevronDown size={12} className="agent-thought-chevron" />
        ) : (
          <ChevronRight size={12} className="agent-thought-chevron" />
        )}
      </button>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            className="agent-thought-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
          >
            <div className="agent-thought-prose">
              <FadingProse text={cleaned} animating={active} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ---------------------------------------------------------------------------
// SSE event → chat-state reducer
// ---------------------------------------------------------------------------

function handleAgentEvent(event, setChatItems) {
  const { type, data } = event;

  if (type === 'text_delta') {
    const chunk = data?.text || '';
    if (!chunk) return;
    setChatItems((prev) => {
      const last = prev[prev.length - 1];
      if (last?.type === 'assistant' && last.isStreaming) {
        return [
          ...prev.slice(0, -1),
          { ...last, text: last.text + chunk },
        ];
      }
      return [
        ...prev,
        { id: newId(), type: 'assistant', text: chunk, isStreaming: true },
      ];
    });
    return;
  }

  if (type === 'tool_start') {
    setChatItems((prev) => {
      const closed = prev.map((it) =>
        it.type === 'assistant' && it.isStreaming
          ? { ...it, isStreaming: false }
          : it,
      );
      const last = closed[closed.length - 1];
      const entry = {
        toolUseId: data.tool_use_id,
        toolName: data.tool_name,
        input: data.input,
        status: 'running',
      };
      if (last?.type === 'tool_group' && last.isRunning) {
        return [
          ...closed.slice(0, -1),
          { ...last, calls: [...last.calls, entry] },
        ];
      }
      return [
        ...closed,
        {
          id: newId(),
          type: 'tool_group',
          calls: [entry],
          isRunning: true,
        },
      ];
    });
    return;
  }

  if (type === 'tool_result') {
    setChatItems((prev) =>
      prev.map((it) => {
        if (it.type !== 'tool_group') return it;
        let touched = false;
        const calls = it.calls.map((c) => {
          if (c.toolUseId !== data.tool_use_id) return c;
          touched = true;
          return {
            ...c,
            result: data.result,
            isError: !!data.is_error,
            status: data.is_error ? 'error' : 'done',
          };
        });
        if (!touched) return it;
        const stillRunning = calls.some((c) => c.status === 'running');
        return { ...it, calls, isRunning: stillRunning };
      }),
    );
    return;
  }

  if (type === 'mapping_complete') {
    // The runtime assembles the final mapping deterministically from
    // collected tool results and emits it here, just before `done`. The
    // LLM no longer transcribes a <mapping> JSON block.
    setChatItems((prev) => {
      const closed = prev.map((it) =>
        it.type === 'assistant' && it.isStreaming
          ? { ...it, isStreaming: false }
          : it.type === 'tool_group' && it.isRunning
            ? { ...it, isRunning: false }
            : it,
      );
      return [
        ...closed,
        {
          id: newId(),
          type: 'mapping_result',
          mapping: data,
        },
      ];
    });
    return;
  }

  if (type === 'done') {
    setChatItems((prev) =>
      prev.map((it) =>
        it.type === 'assistant' && it.isStreaming
          ? { ...it, isStreaming: false }
          : it.type === 'tool_group' && it.isRunning
            ? { ...it, isRunning: false }
            : it,
      ),
    );
    return;
  }

  if (type === 'error') {
    setChatItems((prev) => [
      ...prev,
      {
        id: newId(),
        type: 'assistant',
        text: `⚠ ${data?.message || 'error'}`,
        isStreaming: false,
      },
    ]);
  }
}

export default BalanceSheetAgent;
